import type { DashboardMetadataResource as CreateDashboardMetadataResource } from "@/types_api/backend/transport/create_dashboard_transport_response"
import type {
  DashboardMetadataResource as DashboardLayoutMetadataResource,
  WidgetResource as DashboardLayoutWidgetResource,
} from "@/types_api/backend/transport/dashboard_layout_response"
import type { DashboardMetadataResource as ListDashboardMetadataResource } from "@/types_api/backend/transport/list_dashboard_metadata_response"
import type { DashboardMetadataResource as UpdateDashboardMetadataResource } from "@/types_api/backend/transport/update_dashboard_transport_response"
import { normalizeConversationSource } from "@/types/conversationSource"

import {
  DATA_SOURCE,
  type DashboardMeta,
  type DashboardWidget,
  type DashboardWidgetConfig,
} from "../types/core"
import { mapTransportDatePreferences } from "./transportDashboardDates"

export {
  serializeCreateDashboardRequest,
  serializeDashboardMetadataForTransport,
  serializeDeleteDashboardRequest,
  serializeGetDashboardLayoutRequest,
  serializeReplaceDashboardWidgetsRequest,
  serializeUpdateDashboardRequest,
} from "./transportDashboardSerialization"

export type DashboardMetadataResource =
  | CreateDashboardMetadataResource
  | DashboardLayoutMetadataResource
  | ListDashboardMetadataResource
  | UpdateDashboardMetadataResource

type DashboardWidgetResource = DashboardLayoutWidgetResource

function getTransportResourceId(resourceName: string): string {
  const resourceSegments = resourceName.split("/")
  return resourceSegments[resourceSegments.length - 1] ?? resourceName
}

interface TransportMetricFilterConfig {
  metric_id?: string | null
  metricId?: string | null
  operator?: NonNullable<
    DashboardWidgetConfig["metricFilter"]
  >[number]["operator"]
  value?: number | string | null
  metric_output_type?:
    | NonNullable<
        DashboardWidgetConfig["metricFilter"]
      >[number]["metricOutputType"]
    | null
  metricOutputType?:
    | NonNullable<
        DashboardWidgetConfig["metricFilter"]
      >[number]["metricOutputType"]
    | null
}

interface TransportFilterConfig {
  metric_ids?: string[] | null
  metricIds?: string[] | null
  agent_ids?: string[] | null
  agentIds?: string[] | null
  agent_mutation_ids?: string[] | null
  agentMutationIds?: string[] | null
  persona_ids?: string[] | null
  personaIds?: string[] | null
  template_names?: string[] | null
  templateNames?: string[] | null
  tag_filters?: string[] | null
  tagFilters?: string[] | null
  test_set_ids?: string[] | null
  testSetIds?: string[] | null
  test_case_ids?: string[] | null
  testCaseIds?: string[] | null
  metadata?: Record<string, unknown>[] | null
}

interface TransportDashboardWidgetConfig {
  receiptSource?: DashboardWidgetConfig["receiptSource"]
  githubReceiptView?: DashboardWidgetConfig["githubReceiptView"]
  title?: string | null
  description?: string | null
  text?: string | null
  metric_id?: string | null
  metricId?: string | null
  metric_ids?: string[] | null
  metricIds?: string[] | null
  dataSource?: string | null
  data_source?: string | null
  monitoring?: string | null
  conversation_source?: string | null
  aggregation?: DashboardWidgetConfig["aggregation"] | null
  visualization_type?: DashboardWidgetConfig["visualizationType"] | null
  visualizationType?: DashboardWidgetConfig["visualizationType"] | null
  metric_output_type?: DashboardWidgetConfig["metricOutputType"] | null
  metricOutputType?: DashboardWidgetConfig["metricOutputType"] | null
  group_by?: DashboardWidgetConfig["groupBy"] | null
  groupBy?: DashboardWidgetConfig["groupBy"] | null
  group_by_metadata_key?: string | null
  groupByMetadataKey?: string | null
  split_by?: DashboardWidgetConfig["splitBy"] | null
  splitBy?: DashboardWidgetConfig["splitBy"] | null
  time_mode?: DashboardWidgetConfig["timeMode"] | null
  timeMode?: DashboardWidgetConfig["timeMode"] | null
  filters?: TransportFilterConfig | null
  metric_filter?: TransportMetricFilterConfig[] | null
  metricFilter?: TransportMetricFilterConfig[] | null
  date_range?: DashboardWidgetConfig["dateRange"] | null
  dateRange?: DashboardWidgetConfig["dateRange"] | null
  bucket_interval?: string | null
  bucketInterval?: string | null
  stacked?: boolean | null
  grouped?: boolean | null
  show_as_percentage?: boolean | null
  showAsPercentage?: boolean | null
  show_target_zone?: boolean | null
  showTargetZone?: boolean | null
  show_baseline_bands?: boolean | null
  showBaselineBands?: boolean | null
  baseline_id?: string | null
  baselineId?: string | null
  baseline_sigma_levels?: number[] | null
  baselineSigmaLevels?: number[] | null
  baseline_direction?: DashboardWidgetConfig["baselineDirection"] | null
  baselineDirection?: DashboardWidgetConfig["baselineDirection"] | null
  baseline_band_color?: string | null
  baselineBandColor?: string | null
  x_axis_label?: string | null
  xAxisLabel?: string | null
  y_axis_label?: string | null
  yAxisLabel?: string | null
  custom_color_map?: Record<string, string> | null
  customColorMap?: Record<string, string> | null
  custom_series_names?: Record<string, string> | null
  customSeriesNames?: Record<string, string> | null
  hidden_series?: string[] | null
  hiddenSeries?: string[] | null
  format?: DashboardWidgetConfig["format"] | null
  precision?: number | null
  units?: string | null
  show_count?: boolean | null
  showCount?: boolean | null
  show_range?: boolean | null
  showRange?: boolean | null
  show_std_dev?: boolean | null
  showStdDev?: boolean | null
  show_box_plot?: boolean | null
  showBoxPlot?: boolean | null
  metric_manager?: string | null
  metricManager?: string | null
  color_scheme?: string | null
  colorScheme?: string | null
}

function setIfPresent<T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | null | undefined,
): void {
  if (value != null) {
    target[key] = value
  }
}

function hasProperties(value: object): boolean {
  return Object.keys(value).length > 0
}

function getDataSourceFromTransport(
  config: TransportDashboardWidgetConfig,
): DashboardWidgetConfig["dataSource"] | undefined {
  const conversationSource = normalizeConversationSource(
    config.conversation_source ??
      config.data_source ??
      config.dataSource ??
      config.monitoring,
  )
  if (conversationSource) {
    return conversationSource
  }

  if (
    config.data_source === DATA_SOURCE.HUMAN_REVIEW ||
    config.dataSource === DATA_SOURCE.HUMAN_REVIEW ||
    config.monitoring === "Labeling"
  ) {
    return DATA_SOURCE.HUMAN_REVIEW
  }

  return undefined
}

function mapTransportMetadataFilters(
  metadata?: Record<string, unknown>[] | null,
): NonNullable<DashboardWidgetConfig["filters"]>["metadata"] | undefined {
  if (!metadata?.length) {
    return undefined
  }

  const mappedMetadata = metadata.flatMap((entry) => {
    if (typeof entry.key !== "string" || typeof entry.value !== "string") {
      return []
    }

    return [{ key: entry.key, value: entry.value }]
  })

  return mappedMetadata.length > 0 ? mappedMetadata : undefined
}

function mapTransportFiltersForClient(
  filters?: TransportFilterConfig | null,
): DashboardWidgetConfig["filters"] | undefined {
  if (!filters) {
    return undefined
  }

  const mappedFilters: DashboardWidgetConfig["filters"] = {}
  setIfPresent(
    mappedFilters,
    "metricIds",
    filters.metric_ids ?? filters.metricIds,
  )
  setIfPresent(mappedFilters, "agentIds", filters.agent_ids ?? filters.agentIds)
  setIfPresent(
    mappedFilters,
    "agentMutationIds",
    filters.agent_mutation_ids ?? filters.agentMutationIds,
  )
  setIfPresent(
    mappedFilters,
    "personaIds",
    filters.persona_ids ?? filters.personaIds,
  )
  setIfPresent(
    mappedFilters,
    "templateNames",
    filters.template_names ?? filters.templateNames,
  )
  setIfPresent(
    mappedFilters,
    "tagFilters",
    filters.tag_filters ?? filters.tagFilters,
  )
  setIfPresent(
    mappedFilters,
    "testSetIds",
    filters.test_set_ids ?? filters.testSetIds,
  )
  setIfPresent(
    mappedFilters,
    "testCaseIds",
    filters.test_case_ids ?? filters.testCaseIds,
  )
  setIfPresent(
    mappedFilters,
    "metadata",
    mapTransportMetadataFilters(filters.metadata),
  )

  return hasProperties(mappedFilters) ? mappedFilters : undefined
}

function mapTransportMetricFiltersForClient(
  metricFilters?: TransportMetricFilterConfig[] | null,
): DashboardWidgetConfig["metricFilter"] | undefined {
  if (!metricFilters?.length) {
    return undefined
  }

  const mappedMetricFilters = metricFilters.flatMap((filter) => {
    const metricId = filter.metric_id ?? filter.metricId
    const metricOutputType =
      filter.metric_output_type ?? filter.metricOutputType
    if (
      !metricId ||
      !filter.operator ||
      filter.value == null ||
      !metricOutputType
    ) {
      return []
    }

    return [
      {
        metricId,
        operator: filter.operator,
        value: filter.value,
        metricOutputType,
      },
    ]
  })

  return mappedMetricFilters.length > 0 ? mappedMetricFilters : undefined
}

function mapTransportWidgetConfigForClient(
  config?: TransportDashboardWidgetConfig | null,
): DashboardWidgetConfig | undefined {
  if (!config) {
    return undefined
  }

  const mappedConfig: DashboardWidgetConfig = {}
  const explicitDataSource = getDataSourceFromTransport(config)
  setIfPresent(mappedConfig, "receiptSource", config.receiptSource)
  setIfPresent(mappedConfig, "githubReceiptView", config.githubReceiptView)
  setIfPresent(mappedConfig, "title", config.title)
  setIfPresent(mappedConfig, "description", config.description)
  setIfPresent(mappedConfig, "text", config.text)
  setIfPresent(mappedConfig, "metricId", config.metric_id ?? config.metricId)
  setIfPresent(mappedConfig, "metricIds", config.metric_ids ?? config.metricIds)
  setIfPresent(mappedConfig, "dataSource", explicitDataSource)
  setIfPresent(mappedConfig, "aggregation", config.aggregation)
  setIfPresent(
    mappedConfig,
    "visualizationType",
    config.visualization_type ?? config.visualizationType,
  )
  setIfPresent(
    mappedConfig,
    "metricOutputType",
    config.metric_output_type ?? config.metricOutputType,
  )
  setIfPresent(mappedConfig, "groupBy", config.group_by ?? config.groupBy)
  setIfPresent(
    mappedConfig,
    "groupByMetadataKey",
    config.group_by_metadata_key ?? config.groupByMetadataKey,
  )
  setIfPresent(mappedConfig, "splitBy", config.split_by ?? config.splitBy)
  setIfPresent(mappedConfig, "timeMode", config.time_mode ?? config.timeMode)
  setIfPresent(mappedConfig, "dateRange", config.dateRange ?? config.date_range)
  setIfPresent(
    mappedConfig,
    "bucketInterval",
    config.bucket_interval ?? config.bucketInterval,
  )
  setIfPresent(mappedConfig, "stacked", config.stacked)
  setIfPresent(mappedConfig, "grouped", config.grouped)
  setIfPresent(
    mappedConfig,
    "showAsPercentage",
    config.show_as_percentage ?? config.showAsPercentage,
  )
  setIfPresent(
    mappedConfig,
    "showTargetZone",
    config.show_target_zone ?? config.showTargetZone,
  )
  setIfPresent(
    mappedConfig,
    "showBaselineBands",
    config.show_baseline_bands ?? config.showBaselineBands,
  )
  setIfPresent(
    mappedConfig,
    "baselineId",
    config.baseline_id ?? config.baselineId,
  )
  setIfPresent(
    mappedConfig,
    "baselineSigmaLevels",
    config.baseline_sigma_levels ?? config.baselineSigmaLevels,
  )
  setIfPresent(
    mappedConfig,
    "baselineDirection",
    config.baseline_direction ?? config.baselineDirection,
  )
  setIfPresent(
    mappedConfig,
    "baselineBandColor",
    config.baseline_band_color ?? config.baselineBandColor,
  )
  setIfPresent(
    mappedConfig,
    "xAxisLabel",
    config.x_axis_label ?? config.xAxisLabel,
  )
  setIfPresent(
    mappedConfig,
    "yAxisLabel",
    config.y_axis_label ?? config.yAxisLabel,
  )
  setIfPresent(
    mappedConfig,
    "customColorMap",
    config.custom_color_map ?? config.customColorMap,
  )
  setIfPresent(
    mappedConfig,
    "customSeriesNames",
    config.custom_series_names ?? config.customSeriesNames,
  )
  setIfPresent(
    mappedConfig,
    "hiddenSeries",
    config.hidden_series ?? config.hiddenSeries,
  )
  setIfPresent(mappedConfig, "format", config.format)
  setIfPresent(mappedConfig, "precision", config.precision)
  setIfPresent(mappedConfig, "units", config.units)
  setIfPresent(mappedConfig, "showCount", config.show_count ?? config.showCount)
  setIfPresent(mappedConfig, "showRange", config.show_range ?? config.showRange)
  setIfPresent(
    mappedConfig,
    "showStdDev",
    config.show_std_dev ?? config.showStdDev,
  )
  setIfPresent(
    mappedConfig,
    "showBoxPlot",
    config.show_box_plot ?? config.showBoxPlot,
  )
  setIfPresent(
    mappedConfig,
    "metricManager",
    config.metric_manager ?? config.metricManager,
  )
  setIfPresent(
    mappedConfig,
    "colorScheme",
    config.color_scheme ?? config.colorScheme,
  )

  const filters = mapTransportFiltersForClient(config.filters)
  if (filters) {
    mappedConfig.filters = filters
  }

  const metricFilter = mapTransportMetricFiltersForClient(
    config.metric_filter ?? config.metricFilter,
  )
  if (metricFilter) {
    mappedConfig.metricFilter = metricFilter
  }

  return hasProperties(mappedConfig) ? mappedConfig : undefined
}

export function mapTransportDashboardMeta(
  dashboard: DashboardMetadataResource,
  organizationId?: string,
): DashboardMeta {
  const dashboardConfig = dashboard.config
  const datePreferences = dashboardConfig?.date_preferences

  return {
    attribution: dashboard.attribution,
    id: getTransportResourceId(dashboard.name),
    display_name: dashboard.display_name?.trim() ?? "",
    description: dashboard.description ?? null,
    is_default: dashboard.is_default,
    is_favorite: dashboard.is_favorite,
    created_at: dashboard.create_time,
    last_updated_at: dashboard.update_time,
    organization_id: organizationId ?? "",
    config: dashboardConfig
      ? {
          datePreferences: mapTransportDatePreferences(datePreferences),
        }
      : null,
  }
}

export function mapTransportDashboardWidget(
  widget: DashboardWidgetResource,
): DashboardWidget {
  return {
    id: getTransportResourceId(widget.name),
    type: widget.type,
    title: widget.display_name?.trim() || "Untitled",
    config: mapTransportWidgetConfigForClient(widget.config),
    grid_x: widget.grid_x,
    grid_y: widget.grid_y,
    grid_w: widget.grid_w,
    grid_h: widget.grid_h,
  }
}
